import { z } from 'zod';
import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { salesOrderLinesFingerprintSchema } from '../../sales-order-lines/sales-order-lines-fingerprint.js';
import { optionalUuidString, requireAtLeastOneField, requiredUuidString, } from '../zod-helpers/index.js';
import { SALES_ORDER_LINE_CONSTRAINTS } from './constraints.js';
import { salesOrderLineNoteSchema } from './item-note.js';
import { salesOrderLineQuantitySchema } from './item-quantity.js';
import { salesOrderLineUnitPriceSchema } from './item-unit-price.js';
import { ZERO_REVENUE_LINE_TYPES } from './zero-revenue-line-types.js';
export const MAX_LINE_CHANGES_PER_KIND = 500;
/** The longest Lines edited reason the API stores (trimmed; blank is none). */
export const SALES_ORDER_LINE_EDIT_REASON_MAX_LENGTH = 500;
const lineId = requiredUuidString('A line is required.', 'Invalid line.');
// Strict, so a client still sending a line `name` is refused rather than silently ignored.
export const salesOrderLineAddChangeSchema = z
    .strictObject({
    clientKey: z
        .string()
        .min(1, 'A new line needs a key.')
        .max(64, 'A new line key must be 64 characters or less.')
        // Keys travel inside dotted field-error paths, so a dot would split them.
        .regex(/^[A-Za-z0-9_-]+$/, 'A new line key may use only letters, digits, - and _.'),
    productId: requiredUuidString(`${SALES_ORDER_LINE_CONSTRAINTS.product.label} is required.`, 'Invalid product selection.'),
    qty: salesOrderLineQuantitySchema,
    uomId: optionalUuidString(`Invalid ${SALES_ORDER_LINE_CONSTRAINTS.uom.label.toLowerCase()} selection.`)
        .optional()
        .nullable(),
    typeId: z.enum(SalesOrderItemType, `Invalid ${SALES_ORDER_LINE_CONSTRAINTS.lineType.label.toLowerCase()}.`),
    unitPrice: salesOrderLineUnitPriceSchema,
    note: salesOrderLineNoteSchema,
})
    .superRefine((value, context) => {
    if (ZERO_REVENUE_LINE_TYPES.has(value.typeId) && value.unitPrice !== 0) {
        context.addIssue({
            code: 'custom',
            message: 'Sample and Promo lines must be booked at zero unit price.',
            path: ['unitPrice'],
        });
    }
});
// updateSalesOrderItemInputSchema's fields; strict, so a line name is refused.
export const salesOrderLineUpdatePatchSchema = requireAtLeastOneField(z.strictObject({
    qty: salesOrderLineQuantitySchema.optional(),
    unitPrice: salesOrderLineUnitPriceSchema.optional(),
    typeId: z
        .enum(SalesOrderItemType, `Invalid ${SALES_ORDER_LINE_CONSTRAINTS.lineType.label.toLowerCase()}.`)
        .optional(),
    uomId: optionalUuidString(`Invalid ${SALES_ORDER_LINE_CONSTRAINTS.uom.label.toLowerCase()} selection.`)
        .optional()
        .nullable(),
    note: salesOrderLineNoteSchema,
}));
export const salesOrderLineUpdateChangeSchema = z.object({
    id: lineId,
    patch: salesOrderLineUpdatePatchSchema,
});
// GraphQL sends an omitted list as null, so both read as "nothing of this kind".
function listOf(item, noun) {
    return z
        .array(item)
        .max(MAX_LINE_CHANGES_PER_KIND, `At most ${MAX_LINE_CHANGES_PER_KIND} lines can be ${noun} in one save.`)
        .nullish()
        .transform((value) => value ?? []);
}
function firstRepeat(values) {
    const seen = new Set();
    return values.findIndex((value) => {
        if (seen.has(value))
            return true;
        seen.add(value);
        return false;
    });
}
export const salesOrderLineChangeSetSchema = z
    .object({
    add: listOf(salesOrderLineAddChangeSchema, 'added'),
    update: listOf(salesOrderLineUpdateChangeSchema, 'changed'),
    remove: listOf(lineId, 'removed'),
})
    .superRefine((set, context) => {
    if (set.add.length + set.update.length + set.remove.length === 0) {
        context.addIssue({ code: 'custom', message: 'There are no line changes to save.', path: [] });
    }
    const repeatedKey = firstRepeat(set.add.map((change) => change.clientKey));
    if (repeatedKey !== -1) {
        context.addIssue({
            code: 'custom',
            message: 'Each new line needs its own key.',
            path: ['add', repeatedKey, 'clientKey'],
        });
    }
    const repeatedUpdate = firstRepeat(set.update.map((change) => change.id));
    if (repeatedUpdate !== -1) {
        context.addIssue({
            code: 'custom',
            message: 'A line can be changed only once per save.',
            path: ['update', repeatedUpdate, 'id'],
        });
    }
    const repeatedRemove = firstRepeat(set.remove);
    if (repeatedRemove !== -1) {
        context.addIssue({
            code: 'custom',
            message: 'A line can be removed only once per save.',
            path: ['remove', repeatedRemove],
        });
    }
    const removed = new Set(set.remove);
    set.update.forEach((change, index) => {
        if (removed.has(change.id)) {
            context.addIssue({
                code: 'custom',
                message: "A removed line can't also be changed.",
                path: ['update', index, 'id'],
            });
        }
    });
});
export const salesOrderLineChangesInputSchema = z.object({
    salesOrderId: requiredUuidString(`${SALES_ORDER_LINE_CONSTRAINTS.salesOrder.label} is required.`, 'Invalid sales order selection.'),
    linesFingerprint: salesOrderLinesFingerprintSchema,
    changes: salesOrderLineChangeSetSchema,
    reason: z
        .string()
        .trim()
        .max(SALES_ORDER_LINE_EDIT_REASON_MAX_LENGTH, `Reason must be ${SALES_ORDER_LINE_EDIT_REASON_MAX_LENGTH} characters or less.`)
        .transform((value) => (value === '' ? null : value))
        .nullish(),
});
//# sourceMappingURL=line-change-set-input.js.map